package com.elan.edubridge.ui.shell

/**
 * 스토어 링크와 배지의 정본. 두 앱의 게시 상태가 바뀌면 이 파일 한 곳만 고친다.
 *
 * - 학생·보호자 앱 `com.elan.edubridge`("EduBridge 학원 출결 알림") — Google Play 게시 중. 배지는 스토어 페이지로 간다.
 * - 관리자 앱 `com.elan.edubridge.manager`("EduBridge 관리자") — 2026-09-09 첫 심사 제출. 스토어 URL 은 게시 전까지
 *   Play 가 404 를 돌려주지만, 게시 시점에 웹을 다시 배포하지 않아도 되도록 **미리 링크로 둔다**(사용자 결정 2026-09-09).
 *   다시 심사 중 표시로 돌리려면 [StoreLinks.managerPlay] 를 null 로 — null 인 배지는 클릭할 수 없는 상태 표시로만
 *   그려진다(죽은 링크를 만들지 않는다는 랜딩 §3.1 스펙의 원래 형태).
 * - iOS 앱은 스토어 미등록(release-status v1.0.0 §0, Apple Developer 미가입) — App Store 배지는 계속 "출시 준비 중".
 */
object StoreLinks {
    const val STUDENT_PACKAGE = "com.elan.edubridge"
    const val MANAGER_PACKAGE = "com.elan.edubridge.manager"

    fun playStoreUrl(packageName: String): String = "https://play.google.com/store/apps/details?id=$packageName"

    /** 학생·보호자 앱 Google Play. */
    val studentPlay: String? = playStoreUrl(STUDENT_PACKAGE)

    /** 학생·보호자 앱 App Store — 미출시. */
    val studentAppStore: String? = null

    /** 관리자 앱 Google Play — 게시 전에 미리 걸어 둔 링크(위 설명). 심사 중 표시로 되돌리려면 null. */
    val managerPlay: String? = playStoreUrl(MANAGER_PACKAGE)
}

/** 스토어 리스팅에 올린 앱 이름 그대로(`fastlane/metadata/android/ko-KR/title.txt`). */
const val STUDENT_APP_NAME = "EduBridge 학원 출결 알림"
const val MANAGER_APP_NAME = "EduBridge 관리자"

enum class Store(val label: String, val iconId: String) {
    GOOGLE_PLAY("Google Play", "icon-play"),
    APP_STORE("App Store", "icon-apple"),
}

/**
 * 스토어 배지 하나. [url] 이 있으면 새 탭으로 여는 링크, 없으면 [pendingLabel] 을 단 비활성 표시다.
 * [appName] 은 링크의 접근성 이름에 들어간다 — "Google Play" 만으로는 두 앱 중 어느 것인지 알 수 없다.
 */
fun storeBadge(store: Store, url: String?, appName: String, pendingLabel: String = "출시 준비 중"): String =
    if (url != null) {
        """<a class="eb-store-badge eb-store-badge--live" href="${url.escapeHtml()}" target="_blank" rel="noopener" aria-label="${appName.escapeHtml()} ${store.label}에서 받기">${icon(store.iconId, "md")}<span>${store.label}<br><strong>다운로드</strong></span></a>"""
    } else {
        """<span class="eb-store-badge eb-store-badge--disabled" aria-disabled="true">${icon(store.iconId, "md")}<span>${store.label}<br><strong>${pendingLabel.escapeHtml()}</strong></span></span>"""
    }

/** 학생·보호자 앱 배지 묶음 — 랜딩 앱 섹션·QR 착지·이용 가이드가 함께 쓴다. */
fun studentAppBadges(): String = """
    <div class="eb-app-download__badges">
        ${storeBadge(Store.GOOGLE_PLAY, StoreLinks.studentPlay, STUDENT_APP_NAME)}
        ${storeBadge(Store.APP_STORE, StoreLinks.studentAppStore, STUDENT_APP_NAME)}
    </div>
""".trimIndent()

/** 관리자 앱 배지 — Android 전용이라 Google Play 하나뿐이다. */
fun managerAppBadges(): String = """
    <div class="eb-app-download__badges">
        ${storeBadge(Store.GOOGLE_PLAY, StoreLinks.managerPlay, MANAGER_APP_NAME, pendingLabel = "심사 중")}
    </div>
""".trimIndent()

/** 본문 문장 안에 끼워 넣는 학생·보호자 앱 링크. 스토어가 없으면 이름만 남는다. */
fun studentAppLink(label: String = STUDENT_APP_NAME): String =
    StoreLinks.studentPlay?.let { """<a href="${it.escapeHtml()}" target="_blank" rel="noopener">${label.escapeHtml()}</a>""" }
        ?: label.escapeHtml()

/** 본문 문장 안에 끼워 넣는 관리자 앱 링크. 심사 중이면 이름만 남는다. */
fun managerAppLink(label: String = MANAGER_APP_NAME): String =
    StoreLinks.managerPlay?.let { """<a href="${it.escapeHtml()}" target="_blank" rel="noopener">${label.escapeHtml()}</a>""" }
        ?: label.escapeHtml()
